import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { RootState } from '../../../../app/rootReducer';
import {
  apiEndPoints,
  getReasonsEndPoint,
} from '../../../../common/api.endPoints';
import {
  Decision,
  DecisionType,
  PA,
  RateClass,
  TobaccoClass,
} from '../../../../common/constants/decision';
import { Reason, ReasonsTransform } from '../../../../common/constants/reasons';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { isEmpty, isOtaf } from '../../../../common/helpers';
import { HttpService } from '../../../../common/http.service';
import { IKeyValMap } from '../../../../common/interfaces/IKeyValMap';
import { ProductFactory } from '../../../../common/productConfig/productFactory';
import { SnackBarType } from '../../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../../layout/SnackbarContext';
import { IDecision } from '../../interfaces/IDecision';
import { IFilterItem } from '../../interfaces/IFilterItem';
import { IRiderInfo } from '../../interfaces/IRiderInfo';
import { applyRiderExclusivityRules } from '../riderExclusivity';
import { EditApproval } from './EditApproval';
import { ReviewApproval } from './ReviewApproval';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const ApproveDrawer = ({
  isOpen,
  setIsOpen,
  applicationId,
  hasPIADecision,
  hasFinalDecision,
  editedDecision,
  setDecision,
  updateReason,
  setUpdateReason,
  engineMaxCoverage,
  isRUW,
  isEditLeadingToCLR,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  applicationId: string;
  hasPIADecision: boolean;
  isRUW: boolean;
  hasFinalDecision: boolean;
  editedDecision?: IDecision;
  setDecision?: Dispatch<SetStateAction<Decision | null>>;
  updateReason?: string | null;
  setUpdateReason?: Dispatch<SetStateAction<string | null>>;
  engineMaxCoverage?: number | null;
  isEditLeadingToCLR?: boolean;
}) => {
  const { flags } = useContext(FeatureFlagsContext);

  const appliedForDecision: IDecision | undefined = useSelector(
    (state: RootState) => {
      return state.decision?.decisions?.find(
        d => d.decisionType === DecisionType.AF,
      );
    },
  );
  const initialRateClass: string | undefined = appliedForDecision?.rateClass;
  const initialTobaccoClass: string | undefined =
    appliedForDecision?.tobaccoClass;
  const initialCoverage: number = appliedForDecision
    ? appliedForDecision.coverage
    : 0;
  const initialTerm: number = appliedForDecision ? appliedForDecision.term : 0;
  const initialPa: number = Number(
    appliedForDecision ? appliedForDecision.tableRating : 0,
  );
  const appliedRiders: string[] = appliedForDecision?.eligibleRiders || [];

  const {
    product,
    carrier,
    subCategory,
    decisions,
    createDecisionFailed,
    createDecisionError,
    createDecisionErrorType,
    stateAtSubmission,
  }: {
    product: string;
    carrier: string;
    subCategory: string;
    decisions: IDecision[];
    createDecisionFailed: boolean;
    createDecisionError: string;
    createDecisionErrorType: string;
    stateAtSubmission: string;
  } = useSelector((state: RootState) => {
    return {
      product: state.application.product,
      carrier: state.application.carrier,
      subCategory: state.application.subCategory,
      decisions: state.decision.decisions,
      createDecisionFailed: state.decision.createDecisionFailed,
      createDecisionError: state.decision.createDecisionError,
      createDecisionErrorType: state.decision.createDecisionErrorType,
      stateAtSubmission: state.application.stateAtSubmission,
    };
  }, shallowEqual);

  const isPolicyActivated = useSelector(
    (state: RootState) => state.application?.activated,
  );
  const [isReview, setIsReview] = useState(false);
  const [maxCoverage, setMaxCoverage] = useState(0);
  const [amendment, setAmendment] = useState('');
  const [doctorInfo, setDoctorInfo] = useState('');
  const [doctorInfoNotProvided, setDoctorInfoNotProvided] = useState(false);
  const [fetchStatus, setFetchStatus] = useState('idle');

  // All dropdown options
  const [rateClasses, setRateClasses] = useState([] as IFilterItem[]);
  const [tobaccoClasses, setTobaccoClasses] = useState([] as IFilterItem[]);
  const [paOptions, setPaOptions] = useState([] as IFilterItem[]);

  // Selected dropdown options
  const [selectedRateClass, setSelectedRateClass] =
    useState<IFilterItem | null>(null);
  const [selectedTobaccoClass, setSelectedTobaccoClass] =
    useState<IFilterItem | null>(null);
  const [selectedPaOption, setSelectedPaOption] = useState({} as IFilterItem);
  const [debits, setDebits] = useState<number | null>(null);
  const [riders, setRiders] = useState([] as IRiderInfo[]);

  // OTAF reasons if dropdown is shown
  const [showOtaf, setShowOtaf] = useState(false);
  const [showTobacco, setShowTobacco] = useState(false);
  const [otafReasons, setOtafReasons] = useState<Reason[]>([]);
  const [selectedOtafReasons, setSelectedOtafReasons] = useState(
    [] as string[],
  );
  const [otafDescription, setOtafDescription] = useState('');
  const [mibCodes, setMibCodes] = useState(new Array<string>());

  // CLR halt state
  const [triggerClr, setTriggerClr] = useState(true);
  const [haltClrReasons, setHaltClrReasons] = useState<string[]>([]);

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  // Applied for classes to compare for OTAF
  const [appliedRateClass, setAppliedRateClass] = useState<IFilterItem | null>(
    null,
  );
  const [appliedTobaccoClass, setAppliedTobaccoClass] =
    useState<IFilterItem | null>(null);
  const [appliedPa, setAppliedPa] = useState({} as IFilterItem);

  const hasStandardPlusRating = () => {
    return productConfig?.hasStandardPlusRating(flags);
  };

  const shouldShowTobacco = () => {
    if (selectedRateClass?.label === RateClass.STANDARD) {
      return true;
    } else if (
      selectedRateClass?.label === RateClass.PREFERRED &&
      productConfig?.showTobaccoForPreferred(flags)
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const show = shouldShowTobacco();
    if (show) {
      setShowTobacco(true);
    } else {
      setShowTobacco(false);
      if (!isRUW) {
        setSelectedTobaccoClass({ value: 2, label: TobaccoClass.NONTOBACCO });
      }
    }
  }, [selectedRateClass]);

  useEffect(() => {
    if (appliedRateClass && appliedTobaccoClass && appliedPa) {
      const eligibleRiders = (riders || [])
        .filter(r => r.isEligible)
        .map(r => r.rider);
      setShowOtaf(
        isOtaf(
          selectedRateClass,
          appliedTobaccoClass,
          selectedTobaccoClass,
          selectedPaOption,
          appliedRiders,
          eligibleRiders,
          product,
          stateAtSubmission,
        ),
      );
    }
  }, [
    selectedRateClass,
    selectedTobaccoClass,
    selectedPaOption,
    riders,
    product,
    stateAtSubmission,
  ]);

  useEffect(() => {
    (async () => {
      setFetchStatus('loading');
      const response = await HttpService.getInstance().get(
        getReasonsEndPoint(applicationId),
      );
      if (response && !response.error) {
        const orderReason: [] = response.parsedBody;
        const orderReasonMap: Reason[] = orderReason.map(item => {
          return ReasonsTransform.getReason(item) as Reason;
        });
        setOtafReasons(orderReasonMap as Reason[]);
      }
    })();

    (async () => {
      const data: IKeyValMap = await HttpService.getInstance().get(
        apiEndPoints.rateClasses,
      );

      const items = data.parsedBody;
      if (!items) {
        return;
      }

      const mappedItems = items
        .filter((item: any) => {
          if (item.name === RateClass.STANDARDPLUS) {
            return hasStandardPlusRating();
          }
          return productConfig?.isRateClassAvailable(item.name) ?? true;
        })
        .map((item: any) => {
          return { value: item.order, label: item.name } as IFilterItem;
        })
        .sort((a: IFilterItem, b: IFilterItem) => {
          return (
            rateClassesSort.indexOf(a.label) - rateClassesSort.indexOf(b.label)
          );
        });

      setRateClasses(mappedItems);
    })();

    (async () => {
      const data: IKeyValMap = await HttpService.getInstance().get(
        apiEndPoints.tobaccoClasses,
      );

      const items = data.parsedBody;
      const mappedItems = items.map((item: any) => {
        return { value: item.order, label: item.name } as IFilterItem;
      });
      setTobaccoClasses(mappedItems);
    })();

    (async () => {
      const data: IKeyValMap = await HttpService.getInstance().get(
        apiEndPoints.pa,
      );

      const items = data.parsedBody;
      const mappedItems = items.map((item: any) => {
        return { value: item.order, label: item.name } as IFilterItem;
      });
      setPaOptions(mappedItems);
      setFetchStatus('success');
    })();
  }, []);

  const setInitialState = () => {
    // Initial rate class
    const appliedRateClassFilterItem =
      rateClasses.find(rate => rate.label === initialRateClass) ?? null;
    setAppliedRateClass(appliedRateClassFilterItem);
    setSelectedRateClass(isRUW ? null : appliedRateClassFilterItem);

    // Initial tobacco class
    const appliedTobaccoClassFilterItem =
      tobaccoClasses.find(tobacco => tobacco.label === initialTobaccoClass) ??
      null;
    setAppliedTobaccoClass(appliedTobaccoClassFilterItem);
    setSelectedTobaccoClass(isRUW ? null : appliedTobaccoClassFilterItem);

    if (appliedRateClass?.label === RateClass.STANDARD) {
      setShowTobacco(true);
    } else {
      setShowTobacco(false);
    }
    // Initial table rating select 100 if its undefined
    const appliedPaOptionFilterItem =
      paOptions.find(
        option =>
          option.label ===
          (initialPa ? initialPa.toString() : PA.PA100.toString()),
      ) || ({} as IFilterItem);
    setAppliedPa(appliedPaOptionFilterItem);
    setSelectedPaOption(appliedPaOptionFilterItem);

    const engineDecisions = (decisions || [])
      .filter(x => x.decisionType === DecisionType.ENGINE)
      .sort((a, b) => b.id - a.id);
    const engineDecision = engineDecisions[0];
    if (engineDecision && !isRUW) {
      setDebits(engineDecision.debits ?? 0);
    }
    const finalDecisions = (decisions || [])
      .filter(x => x.decisionType === DecisionType.FINAL)
      .sort((a, b) => b.id - a.id);
    const finalDecision = finalDecisions[0];
    if (!engineDecision && finalDecision) {
      setDebits(finalDecision.debits ?? (isRUW ? null : 0));
    }

    // if we only have engine and appliedFor, we want to pick the engine decision as latest.
    const filteredDecisions = [...(decisions || [])].filter(
      d => d.decisionType !== DecisionType.AF,
    );
    const latestDecision: IDecision | undefined = filteredDecisions.length
      ? filteredDecisions.sort((a, b) => b.id - a.id)[0]
      : appliedForDecision;
    initRiders(latestDecision);
    setMaxCoverage(
      !hasFinalDecision
        ? 0
        : finalDecision
          ? finalDecision.coverage
          : initialCoverage,
    );

    // If decision is being edited take defaults from existing decision.
    if (editedDecision?.decision === Decision.APPROVED) {
      const find = (options: any[], value: any) =>
        options.find(option => option.label === value) || ({} as IFilterItem);

      const rateClass = find(rateClasses, editedDecision.rateClass);
      setSelectedRateClass(rateClass);
      setSelectedPaOption(find(paOptions, editedDecision.tableRating));
      setMaxCoverage(!hasPIADecision ? 0 : editedDecision.coverage);
      setShowTobacco(shouldShowTobacco());
      setSelectedTobaccoClass(
        find(tobaccoClasses, editedDecision.tobaccoClass),
      );

      if (editedDecision.amendment) setAmendment(editedDecision.amendment);
      if (editedDecision.doctorInfo) setDoctorInfo(editedDecision.doctorInfo);
      if (editedDecision.otafDescription)
        setOtafDescription(editedDecision.otafDescription);
      if (editedDecision.otafReasons?.length) {
        setSelectedOtafReasons(editedDecision.otafReasons);
      }
      setDebits(editedDecision.debits ?? (isRUW ? null : 0));

      // Initialize CLR halt state from existing decision
      // haltClr: true means user unchecked "Trigger CLR" checkbox (triggerClr: false)
      if (editedDecision.haltClr !== undefined) {
        setTriggerClr(!editedDecision.haltClr);
      }
      if (editedDecision.haltClrReasons?.length) {
        setHaltClrReasons(editedDecision.haltClrReasons);
      }
    }
  };
  const initRiders = (decision: IDecision | undefined) => {
    if (!decision) {
      return;
    }
    if (decision.ridersInfo && decision.ridersInfo.length > 0) {
      setRiders(decision.ridersInfo);
      return;
    }

    const allRiders: string[] = appliedForDecision?.eligibleRiders || [];

    // For RUW applications, default all riders to eligible so the UW
    // starts from a clean slate. Backend validateRider will catch any
    // rider that cannot be approved given the final decision parameters.
    const ineligibleRiders: string[] = isRUW
      ? []
      : (decision?.ineligibleRiders || []).filter(r => allRiders.includes(r));
    const eligibleRiders: string[] = allRiders.filter(
      r => !ineligibleRiders.includes(r),
    );

    const underwritableSet = new Set(
      productConfig?.getUnderWritableRiders(stateAtSubmission) ?? [],
    );
    const initialRiders = [
      ...eligibleRiders.map(rider => ({
        rider,
        isEligible: true,
        reason: '',
        isUnderwritable: underwritableSet.has(rider),
      })),
      ...ineligibleRiders.map(rider => ({
        rider,
        isEligible: false,
        reason: '',
        isUnderwritable: underwritableSet.has(rider),
      })),
    ].sort((a, b) => a.rider.localeCompare(b.rider));

    const exclusivityRules =
      productConfig?.getRiderExclusivityRules(stateAtSubmission) ?? [];
    setRiders(applyRiderExclusivityRules(initialRiders, exclusivityRules));
  };

  useEffect(() => {
    // Ensure all the information has been fetched and is updated in the state
    if (
      fetchStatus === 'success' &&
      !isEmpty(rateClasses) &&
      !isEmpty(tobaccoClasses) &&
      !isEmpty(paOptions)
    ) {
      setInitialState();
      setFetchStatus('completed');
    }
  }, [fetchStatus, rateClasses, tobaccoClasses, paOptions, isOpen]);

  const { openSnack } = useContext(SnackbarContext);

  useEffect(() => {
    if (createDecisionFailed) {
      openSnack({
        id: uuidv4(),
        type: createDecisionErrorType as SnackBarType,
        message: createDecisionError,
      });
    }
  }, [
    createDecisionFailed,
    createDecisionError,
    createDecisionErrorType,
    openSnack,
  ]);

  const showTobaccoAndHealthClass = () => !!productConfig?.hasRateClass();

  return (
    <>
      {isReview && (
        <ReviewApproval
          applicationId={applicationId}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setIsReview={setIsReview}
          product={product}
          carrier={carrier}
          subCategory={subCategory}
          maxCoverage={maxCoverage}
          term={initialTerm}
          selectedRateClass={
            showTobaccoAndHealthClass() ? selectedRateClass : null
          }
          selectedTobaccoClass={
            showTobaccoAndHealthClass() ? selectedTobaccoClass : null
          }
          selectedPaOption={selectedPaOption}
          debits={debits}
          amendment={amendment}
          doctorInfo={doctorInfo}
          showOtaf={showOtaf}
          selectedOtafReasons={selectedOtafReasons}
          otafDescription={otafDescription}
          mibCodes={mibCodes}
          editedDecision={editedDecision}
          updateReason={updateReason}
          riders={riders}
          engineMaxCoverage={engineMaxCoverage}
          isEditLeadingToCLR={isEditLeadingToCLR}
          triggerClr={triggerClr}
          haltClrReasons={haltClrReasons}
          isPolicyActivated={isPolicyActivated}
        />
      )}
      {!isReview && fetchStatus === 'completed' && (
        <EditApproval
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setIsReview={setIsReview}
          product={product}
          carrier={carrier}
          subCategory={subCategory}
          maxCoverage={maxCoverage}
          setMaxCoverage={setMaxCoverage}
          term={initialTerm}
          rateClasses={rateClasses}
          selectedRateClass={selectedRateClass}
          setSelectedRateClass={setSelectedRateClass}
          tobaccoClasses={tobaccoClasses}
          selectedTobaccoClass={selectedTobaccoClass}
          setSelectedTobaccoClass={setSelectedTobaccoClass}
          paOptions={paOptions}
          selectedPaOption={selectedPaOption}
          setSelectedPaOption={setSelectedPaOption}
          debits={debits}
          setDebits={setDebits}
          amendment={amendment}
          setAmendment={setAmendment}
          doctorInfo={doctorInfo}
          setDoctorInfo={setDoctorInfo}
          doctorInfoNotProvided={doctorInfoNotProvided}
          setDoctorInfoNotProvided={setDoctorInfoNotProvided}
          showOtaf={showOtaf}
          otafReasons={otafReasons}
          selectedOtafReasons={selectedOtafReasons}
          setSelectedOtafReasons={setSelectedOtafReasons}
          otafDescription={otafDescription}
          setOtafDescription={setOtafDescription}
          mibCodes={mibCodes}
          setMibCodes={setMibCodes}
          showTobacco={showTobacco}
          updateReason={updateReason}
          setUpdateReason={setUpdateReason}
          isUpdate={Boolean(editedDecision)}
          setDecision={setDecision}
          isPIA={
            isPolicyActivated ||
            editedDecision?.decisionType === DecisionType.PIA
          }
          riders={riders}
          setRiders={setRiders}
          riderExclusivityRules={
            productConfig?.getRiderExclusivityRules(stateAtSubmission) ?? []
          }
          isRUW={isRUW}
          triggerClr={triggerClr}
          setTriggerClr={setTriggerClr}
          haltClrReasons={haltClrReasons}
          setHaltClrReasons={setHaltClrReasons}
        />
      )}
    </>
  );
};

const rateClassesSort = [
  'PreferredPlus',
  'Preferred Plus',
  'Preferred',
  'StandardPlus',
  'Standard Plus',
  'Standard',
];
